import { z } from 'zod';

import {
  Agent,
  DocumentStatus,
  FeedSource,
  FeedStatus,
  ShopProductStatus,
} from '../../enums';
import type { EndpointContractGroup } from '../schemaUtils';

export const easyAccessSchema = z.object({
  url: z.string(),
});

export type EasyAccess = z.infer<typeof easyAccessSchema>;
export type UrlStatusUpdate = {
  id: string;
  url: string;
  status: DocumentStatus;
};

export const agentsSummaryFeedResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  source: z.nativeEnum(FeedSource),
  status: z.nativeEnum(FeedStatus),
  productStatusCounters: z.object({
    pending: z.number(),
    embedding: z.number(),
    done: z.number(),
    failed: z.number(),
  }),
  products: z.array(
    z.object({
      id: z.string().uuid(),
      url: z.string(),
      name: z.string(),
      status: z.nativeEnum(ShopProductStatus),
      price: z.number().nullish(),
      currency: z.string().nullish(),
    }),
  ),
  productsCount: z.number(),
});

export const agentsSummaryResponseSchema = z.object({
  webUrl: z.string(),
  agent: z.object({
    id: z.string().uuid(),
    name: z.string(),
    agent: z.nativeEnum(Agent),
  }),
  documents: z.array(
    z.object({
      id: z.string().uuid(),
      uri: z.string(),
      name: z.string().optional(),
      publicUrl: z.string(),
      status: z.nativeEnum(DocumentStatus),
    }),
  ),
  documentsCount: z.number(),
  feeds: z.array(agentsSummaryFeedResponseSchema).nullish(),
});

export type AgentsSummaryResponse = z.infer<typeof agentsSummaryResponseSchema>;
export type AgentsSummaryFeedResponse = z.infer<
  typeof agentsSummaryFeedResponseSchema
>;

/**
 * Schema definition for all agents endpoints
 */
export const agentsApiSchemas = {
  summary: {
    path: '/agents/:agentId/summary',
    method: 'GET',
    schema: {
      params: z.object({
        agentId: z.string().uuid(),
      }),
      response: {
        200: agentsSummaryResponseSchema,
      },
    },
  },
  update: {
    path: '/agents/:chatId',
    method: 'PATCH',
    schema: {
      params: z.object({
        chatId: z.string().uuid(),
      }),
      body: z
        .object({
          agent: z.nativeEnum(Agent),
        })
        .strict(),
    },
  },
} satisfies EndpointContractGroup;
